// Catalog.ID Client — Voucher Code Display
// ============================================================================
// The one place a batch of freshly issued voucher codes is put on screen.
//
// Three surfaces show codes and every one of them shows them exactly once, at
// the moment of issue: the anonymous `/vouchers` route, the operator mint pane,
// and the organization's Vouchers pane. The server keeps only SHA-256 hashes, so
// there is no "show it again" anywhere in the system — which makes the wording
// of the warning, and the presence of a copy control on every single row, a
// correctness concern rather than a styling one. Hence one implementation.
//
// The copy control always copies the CANONICAL code, never the grouped display
// form: the canonical form is what a redeem field expects (the server folds
// either, but there is no reason to make it work harder).

import { createElement, createCopyButton } from '@catalog-shell';
import { formatVoucherCode } from './voucher-code';

/** Heading for a batch of `count` codes. Singular below two, as everywhere else. */
export function voucherCodesHeading(count: number, noun = 'voucher code'): string {
    return count === 1 ? `Your ${noun}` : `Your ${count} ${noun}s`;
}

/**
 * The "this is the only copy" warning. Deliberately states BOTH facts a holder
 * needs — that nothing is emailed and the page is the only place the code
 * appears, and when it stops working.
 */
export function voucherCodesNotice(expiresAt: string | Date | null | undefined): string {
    const base = 'Copy these now — they are not emailed and this is the only place they appear. '
        + 'Each one pays for one registration';
    const date = expiresAt === null || expiresAt === undefined ? null : new Date(expiresAt);
    if (!date || Number.isNaN(date.getTime())) return `${base}.`;
    return `${base} and is valid until ${date.toLocaleDateString()}.`;
}

export interface VoucherCodeDisplayOptions {
    /** Drives the expiry sentence. Omit to show the warning without a date. */
    expiresAt?: string | Date | null;
    /** Heading tag — panes sit inside a field group and want a shallower level. */
    headingTag?: 'h3' | 'h4';
    /** Noun in the heading, e.g. 'delegate voucher'. */
    noun?: string;
    /** Replace the heading outright — the operator pane mints FOR someone else, so
     *  "Your …" would be wrong there. */
    heading?: string;
    /** Set false where the surrounding copy already carries the warning. */
    showNotice?: boolean;
}

/**
 * Render `codes` into `container`, replacing whatever was there. Returns the
 * container so a caller can keep chaining.
 */
export function renderVoucherCodes(
    container: HTMLElement,
    codes: string[],
    opts: VoucherCodeDisplayOptions = {},
): HTMLElement {
    const {
        expiresAt = null, headingTag = 'h3', noun = 'voucher code', showNotice = true, heading,
    } = opts;

    container.innerHTML = '';
    container.appendChild(createElement(headingTag, {
        className: 'cid-wizard-heading',
        textContent: heading ?? voucherCodesHeading(codes.length, noun),
    }));
    if (showNotice) {
        container.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: voucherCodesNotice(expiresAt),
        }));
    }

    for (const code of codes) {
        const row = createElement('div', { className: 'cid-auth-field' });
        row.appendChild(createElement('code', {
            className: 'cid-wizard-cid',
            textContent: formatVoucherCode(code),
        }));
        row.appendChild(createCopyButton({ getText: () => code, label: 'Copy' }));
        container.appendChild(row);
    }

    if (codes.length > 1) {
        container.appendChild(createCopyButton({
            getText: () => codes.join('\n'),
            label: 'Copy all',
        }));
    }
    return container;
}
